// The Budget filter of the search bar: four fixed price brackets, per night, in US dollars.
//
// Oran / Moriya, 12 Oct 2026: "replace the current Budget (USD) slider/drop-down in the search
// bar with fixed per-night brackets". The agent ticks one or more; with none ticked nothing is
// filtered, and that is how every search opens. The slider it replaces opened every search on
// "$2,000 - $50,000+", so each villa under $2,000 a night was hidden without the agent having
// chosen it, and the saved search state put the $2,000 back when it was taken out of the address.
//
// One table, and everything else is built from it: the tick-boxes, the label on the button, the
// stored choice, the page address and the request. The hubs filter on the price the search card
// shows, with markup (VTHub/SHub api/utils/priceBudget.js): per night without dates, the stay
// total with dates, so with dates the request carries the brackets multiplied by the nights.

export const PRICE_BRACKETS = [
  { id: 'b400', min: 400, max: 2500 },
  { id: 'b2500', min: 2500, max: 10000 },
  { id: 'b10000', min: 10000, max: 20000 },
  { id: 'b20000', min: 20000, max: null }, // no upper limit
];

const money = (n) => `$${Number(n).toLocaleString('en-US')}`;

/** "$400 – $2,500", and "$20,000+" where there is no upper limit. */
export const rangeLabel = (min, max) => (max == null ? `${money(min)}+` : `${money(min)} – ${money(max)}`);

const toItem = (b) => ({ value: b.id, label: rangeLabel(b.min, b.max), min: b.min, max: b.max });

/**
 * The ticked brackets as the items the rest of the search carries ({value, label, min, max}),
 * always in the table's order and always built from the table. Takes ids or items; anything
 * that is not one of the four brackets is left out, which is what drops a choice saved by the
 * old slider ({value: "custom", min: 2000}) and the still older "PriceRange3" links.
 */
export const bracketItems = (picked) => {
  const want = new Set((Array.isArray(picked) ? picked : []).map((x) => String(x?.value ?? x)));
  return PRICE_BRACKETS.filter((b) => want.has(b.id)).map(toItem);
};

/**
 * The ticked brackets as price ranges, neighbours joined: "$400 – $2,500" and "$2,500 – $10,000"
 * are one range, $400 to $10,000. Four brackets can leave two ranges at most.
 */
export const mergedRanges = (picked) => {
  const out = [];
  for (const b of bracketItems(picked)) {
    const last = out[out.length - 1];
    if (last && (last.max == null || b.min <= last.max)) {
      last.max = last.max == null || b.max == null ? null : Math.max(last.max, b.max);
    } else {
      out.push({ min: b.min, max: b.max });
    }
  }
  return out;
};

/** What the Budget button says: "Any price", or the ranges in force. */
export const budgetSummary = (picked) => {
  const ranges = mergedRanges(picked);
  return ranges.length ? ranges.map((r) => rangeLabel(r.min, r.max)).join(', ') : 'Any price';
};

/** Is a price per night inside one of the ticked brackets? True when none is ticked. */
export const inPriceBrackets = (picked, perNight) => {
  const ranges = mergedRanges(picked);
  if (!ranges.length) return true;
  const v = Number(perNight);
  return Number.isFinite(v) && ranges.some((r) => v >= r.min && (r.max == null || v <= r.max));
};

/**
 * What a search sends the hubs, or null when no bracket is ticked.
 *   nights 1 (a search without dates): the brackets as they are, per night.
 *   nights N (a search with dates): multiplied by N, because with dates the hubs compare the
 *   stay total.
 * priceMin / priceMax are the outer bounds. When the ticked brackets leave a gap ("$400 –
 * $2,500" with "$20,000+") the ranges themselves travel beside them as priceRanges, and the
 * hubs then use those; a hub that does not know priceRanges falls back on the outer bounds.
 */
export const priceRequestBounds = (picked, nights = 1) => {
  const ranges = mergedRanges(picked);
  if (!ranges.length) return null;
  const n = Number(nights) > 0 ? Number(nights) : 1;
  const out = { priceMin: ranges[0].min * n };
  const top = ranges[ranges.length - 1].max;
  if (top != null) out.priceMax = top * n;
  if (ranges.length > 1) out.priceRanges = ranges.map((r) => [r.min * n, r.max == null ? null : r.max * n]);
  return out;
};

/**
 * One bracket in another currency, for display only: "about ₪1,480 – ₪9,250". The brackets are
 * in US dollars and the hubs filter in dollars; this line tells an agent who works in shekels
 * or euro roughly where they fall. `rate` is units of that currency per dollar. "" for dollars,
 * for an unknown rate, and wherever a figure cannot be worked out.
 */
export const bracketInCurrency = (bracket, { code, rate, prefix } = {}) => {
  const r = Number(rate);
  if (!bracket || !code || code === 'USD' || !(r > 0)) return '';
  const round = (usd) => {
    const v = usd * r;
    const step = v >= 100000 ? 1000 : v >= 10000 ? 100 : v >= 1000 ? 10 : 1;
    return (Math.round(v / step) * step).toLocaleString('en-US');
  };
  const sign = prefix || `${code} `;
  return bracket.max == null
    ? `about ${sign}${round(bracket.min)}+`
    : `about ${sign}${round(bracket.min)} – ${sign}${round(bracket.max)}`;
};

const STORE_KEY = 'selectedPrices';
// The slider marked its seeded $2,000 with this key. Nothing reads it any more.
const OLD_DEFAULT_MARK = 'selectedPricesDefaulted';

const rawStored = () => {
  try { return typeof localStorage === 'undefined' ? null : localStorage.getItem(STORE_KEY); } catch (e) { return null; }
};

/**
 * The ticked brackets as stored in this browser, which is what the search request reads
 * (store/redux/Property/service.js). Nothing stored, a cleared choice, a corrupt value or
 * anything saved by the old slider all read as "no bracket": no price filter.
 */
export const readStoredPriceItems = () => {
  const raw = rawStored();
  if (raw === null) return [];
  try { return bracketItems(JSON.parse(raw)); } catch (e) { return []; }
};

/** No bracket is ticked, so nothing the agent chose narrows the search by price. */
export const isPriceDefaulted = () => readStoredPriceItems().length === 0;

/** How a search opens, and what Clear goes back to: no bracket. */
export const defaultPriceItems = () => [];

/** Every change the agent makes to the brackets is stored through here. */
export const writePriceSelection = (items) => {
  const clean = bracketItems(items);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORE_KEY, JSON.stringify(clean));
      localStorage.removeItem(OLD_DEFAULT_MARK);
    }
  } catch (e) { /* private mode: the choice still applies in memory */ }
  return clean;
};

/**
 * Called on every page load (the reducer's first state) and by the "start a fresh search"
 * paths. Returns the brackets in force. It is also where a browser that still holds the old
 * slider's value gets the default back, once: whatever is stored that is not a bracket (the
 * seeded "$2,000 - $50,000+", an agent's old custom range) is rewritten as what it now means.
 */
export const initPriceDefault = () => {
  const items = readStoredPriceItems();
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(STORE_KEY);
      const clean = JSON.stringify(items);
      if (raw !== null && raw !== clean) localStorage.setItem(STORE_KEY, clean);
      localStorage.removeItem(OLD_DEFAULT_MARK);
    }
  } catch (e) { /* nothing to tidy */ }
  return items;
};
